/**
 * [C33] Request schemas of the D07 add-ons — Store (`/store`) and Installments (`/installments`) — over the C30
 * engine (D07 §5; D-105 – D-117, D-290 – D-297; engineer decisions D-309 – D-315).
 *
 * The screens address stock by **product × warehouse**; the API resolves the inventory account (D-292) and fills
 * every server-owned input itself (the contract remaining, the sale record of an invoice return), so none of
 * those can be forged by a client (D-310).
 */
import { z } from "zod";
import { decimalSchema, newCustomerAccountSchema, pinSchema, supervisorSchema, uuidSchema } from "./schemas.js";

const officeDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const unitsSchema = z.string().regex(/^[1-9]\d{0,8}$/, "UNITS_STRING"); // D-110: integer > 0
const textSchema = (max: number) => z.string().trim().max(max);
const optionalDecimal = z.union([decimalSchema, z.literal("")]).optional();

/** The engine's settlement spec (cash and / or a customer account; D-112 cash + on-account = total, surplus D-073). */
export const d07SettlementSchema = z
  .object({
    cashAccountId: uuidSchema.optional(),
    custodyUserId: uuidSchema.optional(),
    cash: optionalDecimal,
    onAccount: optionalDecimal,
    party: z
      .object({
        accountId: z.string().min(1).max(60), // a ledger id or a "new:<ref>" placeholder (D-245 (e))
        // D-073 / D-078 surplus: onto another account (the person's opposite account, an existing one or a "new:<ref>"), or in cash
        surplus: z.union([z.object({ kind: z.literal("account"), accountId: z.string().min(1).max(60) }).strict(), z.object({ kind: z.literal("cash") }).strict()]).optional(),
      })
      .strict()
      .optional(),
  })
  .strict();

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const warehouseCreateSchema = z.object({ name: textSchema(80).min(1), location: textSchema(120).optional(), description: textSchema(300).optional(), branchId: uuidSchema.optional() }).strict();
export const warehouseUpdateSchema = z.object({ id: uuidSchema, name: textSchema(80).min(1).optional(), location: textSchema(120).nullable().optional(), description: textSchema(300).nullable().optional() }).strict();
export const warehouseArchiveSchema = z.object({ id: uuidSchema, archive: z.boolean(), pin: pinSchema.optional(), supervisor: supervisorSchema.optional() }).strict();
export const productCreateSchema = z.object({ name: textSchema(120).min(1), salePrice: decimalSchema.default("0") }).strict();
export const productUpdateSchema = z.object({ id: uuidSchema, name: textSchema(120).min(1).optional(), salePrice: decimalSchema.optional() }).strict();
export const productArchiveSchema = z.object({ id: uuidSchema, archive: z.boolean(), pin: pinSchema.optional(), supervisor: supervisorSchema.optional() }).strict();

const stockRef = { productId: uuidSchema, warehouseId: uuidSchema };

export const storePurchaseInputSchema = z
  .object({
    items: z
      .array(z.object({ ...stockRef, quantity: unitsSchema, unitCost: decimalSchema, salePrice: decimalSchema.optional() }).strict())
      .min(1)
      .max(100),
    funding: d07SettlementSchema.extend({ kind: z.enum(["cash", "supplier", "from_trust"]).optional() }).strict(),
    note: textSchema(300).optional(),
  })
  .strict();

export const storeSaleInputSchema = z
  .object({
    items: z.array(z.object({ ...stockRef, quantity: unitsSchema, unitPrice: decimalSchema }).strict()).min(1).max(100),
    discount: optionalDecimal,
    payment: d07SettlementSchema,
    note: textSchema(300).optional(),
  })
  .strict();

export const storeReturnInputSchema = z
  .object({
    saleOperationId: uuidSchema.optional(),
    items: z.array(z.object({ ...stockRef, quantity: unitsSchema, unitRefund: decimalSchema }).strict()).min(1).max(100),
    refund: d07SettlementSchema,
    reason: textSchema(300).optional(),
    note: textSchema(300).optional(),
  })
  .strict();

export const storeTransferInputSchema = z.object({ productId: uuidSchema, fromWarehouseId: uuidSchema, toWarehouseId: uuidSchema, quantity: unitsSchema, note: textSchema(300).optional() }).strict();
export const storeWriteoffInputSchema = z.object({ ...stockRef, quantity: unitsSchema, reason: textSchema(300).min(1) }).strict();

/** Buyer details typed on a sale (old card sale): kept in `core.store_party`, phone / address encrypted (D-245). */
export const storeBuyerSchema = z.object({ name: textSchema(120).optional(), phone: textSchema(40).optional(), address: textSchema(200).optional() }).strict();

export const STORE_TYPES = ["store_purchase", "store_sale", "store_return", "store_transfer", "store_writeoff"] as const;
export type StoreType = (typeof STORE_TYPES)[number];

export const storePreviewSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("store_purchase"), input: storePurchaseInputSchema, branchId: uuidSchema.optional(), newAccounts: z.array(newCustomerAccountSchema).max(3).optional() }).strict(),
  z.object({ type: z.literal("store_sale"), input: storeSaleInputSchema, buyer: storeBuyerSchema.optional(), branchId: uuidSchema.optional(), newAccounts: z.array(newCustomerAccountSchema).max(3).optional() }).strict(),
  z.object({ type: z.literal("store_return"), input: storeReturnInputSchema, branchId: uuidSchema.optional(), newAccounts: z.array(newCustomerAccountSchema).max(3).optional() }).strict(),
  z.object({ type: z.literal("store_transfer"), input: storeTransferInputSchema, branchId: uuidSchema.optional() }).strict(),
  z.object({ type: z.literal("store_writeoff"), input: storeWriteoffInputSchema, branchId: uuidSchema.optional() }).strict(),
]);
export type StorePreviewInput = z.infer<typeof storePreviewSchema>;

export const d07CommitSchema = z.object({ previewId: uuidSchema, idempotencyKey: uuidSchema, pin: pinSchema.optional(), supervisor: supervisorSchema.optional() }).strict();
export const storeCancelSchema = z.object({ operationId: uuidSchema, reason: z.string().trim().min(1).max(500), idempotencyKey: uuidSchema, pin: pinSchema }).strict();
export const d07PrintSchema = z.object({ channel: z.enum(["print", "pdf", "whatsapp"]) }).strict();
/** [C35] the receipt window reads the receipt with `view` (nothing issued to the customer, nothing recorded), as the customer entry voucher does. */
export const d07ReceiptPrintSchema = z.object({ channel: z.enum(["view", "print", "pdf", "whatsapp"]) }).strict();
export const storeOperationsQuerySchema = z.object({ from: officeDateSchema.optional(), to: officeDateSchema.optional(), branchId: uuidSchema.optional() }).strict();

// ---------------------------------------------------------------------------
// Installments
// ---------------------------------------------------------------------------

export const contractItemSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("none") }).strict(),
  z.object({ kind: z.literal("product"), productId: uuidSchema, warehouseId: uuidSchema, quantity: unitsSchema.default("1") }).strict(),
  z.object({ kind: z.literal("card"), boxAccountId: uuidSchema, custodyUserId: uuidSchema.optional() }).strict(),
  z.object({ kind: z.literal("currency"), stockAccountId: uuidSchema, quantity: decimalSchema, sellRate: decimalSchema.optional(), custodyUserId: uuidSchema.optional() }).strict(),
]);

export const contractPreviewSchema = z
  .object({
    /** The customer's account (debt or trust, D-293 / T-12) by ledger id, or "new:<ref>" with `newAccounts`. */
    customerAccountId: z.string().min(1).max(60),
    itemName: textSchema(160).min(1),
    item: contractItemSchema,
    basePrice: decimalSchema,
    profitPct: decimalSchema.default("0"),
    deductionPct: decimalSchema.default("0"),
    downPayment: decimalSchema.default("0"),
    downPaymentAccountId: uuidSchema.optional(),
    custodyUserId: uuidSchema.optional(),
    period: z.enum(["daily", "weekly", "monthly"]).default("monthly"),
    installments: z.number().int().min(1).max(600),
    firstDueDate: officeDateSchema,
    note: textSchema(500).optional(),
    branchId: uuidSchema.optional(),
    newAccounts: z.array(newCustomerAccountSchema).max(2).optional(),
  })
  .strict();
export type ContractPreviewInput = z.infer<typeof contractPreviewSchema>;

export const paymentPreviewSchema = z
  .object({ contractId: uuidSchema, amount: decimalSchema, boxAccountId: uuidSchema, custodyUserId: uuidSchema.optional(), note: textSchema(300).optional() })
  .strict();

export const contractCancelSchema = z.object({ contractId: uuidSchema, reason: z.string().trim().min(1).max(500), idempotencyKey: uuidSchema, pin: pinSchema }).strict();
export const paymentCancelSchema = z.object({ operationId: uuidSchema, reason: z.string().trim().min(1).max(500), idempotencyKey: uuidSchema, pin: pinSchema }).strict();
/** D-109 (3): set one unpaid row (the following unpaid rows absorb the difference) or spread the unpaid rows evenly ("إعادة توزيع"). */
export const scheduleEditSchema = z
  .object({
    contractId: uuidSchema,
    rowNo: z.number().int().min(1).optional(),
    amount: decimalSchema.optional(),
    redistribute: z.boolean().optional(),
    pin: pinSchema.optional(),
    supervisor: supervisorSchema.optional(),
  })
  .strict()
  .refine((v) => (v.redistribute === true) !== (v.rowNo !== undefined && v.amount !== undefined), { message: "ROW_XOR_REDISTRIBUTE" });
